import { FC, useMemo } from "react";
import {
  Box,
  Stack,
  Typography,
  TableCell,
  TableRow,
  CircularProgress,
  IconButton,
  Chip,
  Tooltip,
  Collapse,
  Button,
} from "@mui/material";
import {
  KeyboardArrowDown,
  KeyboardArrowRight,
  Description,
  Download,
  AttachMoney,
  Gavel,
} from "@mui/icons-material";
import DeleteIcon from "@/assets/svgs/garbage.svg";
import {
  getDocumentTypeLabel,
  getDocumentTypeFullLabel,
  getStatusLabel,
  getAuditStatusStyle,
  getAuditStatusLabel,
  getAuditStatusIcon,
} from "@/utils/customs-helpers";
import { tokens } from "@/tokens";

const COLUMN_COUNT = 8;

export interface ClusterRowProps {
  cluster: CustomsCluster;
  documents: CustomsDocument[];
  isExpanded: boolean;
  onToggle: () => void;
  onDownload: (doc: CustomsDocument) => void;
  onDelete: (doc: CustomsDocument) => void;
  onDeleteCluster: (cluster: CustomsCluster) => void;
  loadingStates: {
    delete: string[];
    download: string[];
    audit: string[];
    deleteCluster: string[];
  };
  onRunAudit: (clusterId: string) => void;
  onViewAudit: (clusterId: string, audit?: ClusterAudit) => void;
  audit: ClusterAudit | null;
  onDocumentClick?: (doc: CustomsDocument) => void;
}

export const ClusterRow: FC<ClusterRowProps> = ({
  cluster,
  documents,
  isExpanded,
  onToggle,
  onDownload,
  onDelete,
  onDeleteCluster,
  loadingStates,
  onRunAudit,
  onViewAudit,
  audit,
  onDocumentClick,
}) => {
  const entryNumber = cluster.entryNumbers?.[0] || "No Entry Number";
  const isAuditing = loadingStates.audit.includes(cluster._id);
  const isDeletingCluster = loadingStates.deleteCluster.includes(cluster._id);

  // Extract identifiers from clusterKeys as a flat list (excluding entry-related types)
  const identifiers = useMemo((): { type: string; label: string; value: string }[] => {
    const result: { type: string; label: string; value: string }[] = [];

    cluster.clusterKeys?.forEach((key) => {
      if (key.value) {
        // Skip entry-related identifiers since we already group by entry number
        if ((key.type as string) === "ENTRY" || (key.type as string) === "ENTRY_NUMBER") return;

        let label: string = key.type;
        if (key.type === "BL") label = "B/L";
        else if (key.type === "CONTAINER") label = "Container";
        else if (key.type === "PO") label = "PO";
        else if (key.type === "INV") label = "Invoice";

        result.push({ type: key.type, label, value: key.value });
      }
    });

    return result;
  }, [cluster.clusterKeys]);

  // Get unique document types for this cluster
  const documentTypes = useMemo(() => {
    const types = new Set<CustomsDocumentType>();
    documents.forEach((doc) => {
      if (doc.documentType) types.add(doc.documentType);
    });
    return Array.from(types);
  }, [documents]);

  const auditStatus = (audit?.status ?? cluster.latestAuditStatus) as
    | ClusterAuditStatus
    | undefined;
  const queueStatus = cluster.auditQueueStatus;
  const isQueued = queueStatus === "QUEUED";
  const isProcessing = queueStatus === "PROCESSING";
  const isInQueue = isQueued || isProcessing;
  const hasAudit = audit || cluster.latestAuditStatus;

  let buttonLabel = hasAudit ? "Re-audit" : "Audit";
  if (isAuditing) buttonLabel = "...";
  else if (isProcessing) buttonLabel = "Running";
  else if (isQueued) buttonLabel = "Queued";

  return (
    <>
      {/* Summary Row */}
      <TableRow
        onClick={onToggle}
        sx={{
          cursor: "pointer",
          backgroundColor: isExpanded ? tokens.bg.hover : tokens.bg.surface,
          transition: "background-color 150ms ease",
          "&:hover": {
            backgroundColor: tokens.bg.hover,
          },
          "& > td": {
            borderBottom: isExpanded ? "none" : `1px solid ${tokens.border.light}`,
          },
        }}
      >
        {/* Expand Icon */}
        <TableCell sx={{ width: 48, p: 1, pl: 1.5 }}>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onToggle();
            }}
            sx={{ p: 0.5 }}
          >
            {isExpanded ? (
              <KeyboardArrowDown sx={{ color: tokens.brand.primary, fontSize: 20 }} />
            ) : (
              <KeyboardArrowRight sx={{ color: tokens.text.secondary, fontSize: 20 }} />
            )}
          </IconButton>
        </TableCell>

        {/* Entry Number */}
        <TableCell sx={{ fontWeight: tokens.fontWeight.bold, fontSize: tokens.fontSize.base, color: tokens.text.primary }}>
          {entryNumber}
        </TableCell>

        {/* Identifiers */}
        <TableCell sx={{ maxWidth: 300 }}>
          {identifiers.length > 0 ? (
            <Stack direction="row" gap={0.5} flexWrap="wrap">
              {identifiers.slice(0, 4).map((id, idx) => (
                <Tooltip key={idx} title={`${id.label}: ${id.value}`} arrow>
                  <Chip
                    label={
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <Typography
                          component="span"
                          sx={{ fontSize: tokens.fontSize["2xs"], fontWeight: tokens.fontWeight.bold, color: tokens.text.secondary, textTransform: "uppercase", letterSpacing: "0.02em" }}
                        >
                          {id.label}
                        </Typography>
                        <Typography
                          component="span"
                          sx={{
                            fontSize: tokens.fontSize["2xs"],
                            fontWeight: tokens.fontWeight.semibold,
                            color: tokens.text.primary,
                            maxWidth: 90,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {id.value}
                        </Typography>
                      </Box>
                    }
                    size="small"
                    sx={{
                      height: 22,
                      bgcolor: tokens.bg.muted,
                      "& .MuiChip-label": { px: 1 },
                    }}
                  />
                </Tooltip>
              ))}
              {identifiers.length > 4 && (
                <Tooltip
                  title={identifiers
                    .slice(4)
                    .map((id) => `${id.label}: ${id.value}`)
                    .join("\n")}
                  arrow
                >
                  <Chip
                    label={`+${identifiers.length - 4}`}
                    size="small"
                    sx={{
                      height: 22,
                      fontSize: tokens.fontSize["2xs"],
                      bgcolor: tokens.border.default,
                      "& .MuiChip-label": { px: 0.75 },
                    }}
                  />
                </Tooltip>
              )}
            </Stack>
          ) : (
            <Typography sx={{ color: tokens.text.muted, fontSize: tokens.fontSize.base }}>-</Typography>
          )}
        </TableCell>

        {/* Document Count */}
        <TableCell align="center">
          <Typography sx={{ fontSize: tokens.fontSize.base, fontWeight: tokens.fontWeight.semibold, color: tokens.text.body }}>
            {documents.length}
          </Typography>
        </TableCell>

        {/* Document Types */}
        <TableCell>
          <Stack direction="row" gap={0.5} flexWrap="wrap">
            {documentTypes.map((type) => (
              <Chip
                key={type}
                label={getDocumentTypeLabel(type)}
                size="small"
                sx={{
                  height: 20,
                  fontSize: tokens.fontSize["2xs"],
                  fontWeight: tokens.fontWeight.semibold,
                  bgcolor: tokens.bg.muted,
                  color: tokens.text.body,
                }}
              />
            ))}
          </Stack>
        </TableCell>

        {/* Audit Status */}
        <TableCell align="center">
          {auditStatus ? (
            <Stack direction="row" alignItems="center" justifyContent="center" gap={0.5} flexWrap="wrap">
              <Tooltip title="Click to view audit details">
                <Chip
                  icon={getAuditStatusIcon(auditStatus) || undefined}
                  label={getAuditStatusLabel(auditStatus, true)}
                  size="small"
                  variant="outlined"
                  onClick={(e) => {
                    e.stopPropagation();
                    onViewAudit(cluster._id, audit ?? undefined);
                  }}
                  sx={{
                    height: 20,
                    fontSize: tokens.fontSize["2xs"],
                    fontWeight: tokens.fontWeight.semibold,
                    cursor: "pointer",
                    bgcolor: "transparent",
                    color: getAuditStatusStyle(auditStatus).color,
                    borderColor: getAuditStatusStyle(auditStatus).borderColor,
                    "& .MuiChip-icon": {
                      color: getAuditStatusStyle(auditStatus).color,
                      marginLeft: "6px",
                    },
                  }}
                />
              </Tooltip>
              {cluster.hasValueDiscrepancies && (
                <Tooltip title="Value discrepancy detected in audit">
                  <Chip
                    icon={<AttachMoney sx={{ fontSize: 14 }} />}
                    label="Value"
                    size="small"
                    sx={{
                      height: 20,
                      fontSize: tokens.fontSize["2xs"],
                      fontWeight: tokens.fontWeight.bold,
                      bgcolor: `${tokens.status.warning}14`,
                      color: tokens.status.warning,
                      borderColor: `${tokens.status.warning}40`,
                      border: "1px solid",
                      "& .MuiChip-icon": { color: tokens.status.warning, ml: "4px" },
                      "& .MuiChip-label": { px: 0.5, pr: 1 },
                    }}
                  />
                </Tooltip>
              )}
              {cluster.hasClassificationDiscrepancies && (
                <Tooltip title="HTS classification discrepancy detected in audit">
                  <Chip
                    icon={<Gavel sx={{ fontSize: 13 }} />}
                    label="HTS"
                    size="small"
                    sx={{
                      height: 20,
                      fontSize: tokens.fontSize["2xs"],
                      fontWeight: tokens.fontWeight.bold,
                      bgcolor: `${tokens.status.danger}14`,
                      color: tokens.status.danger,
                      borderColor: `${tokens.status.danger}40`,
                      border: "1px solid",
                      "& .MuiChip-icon": { color: tokens.status.danger, ml: "4px" },
                      "& .MuiChip-label": { px: 0.5, pr: 1 },
                    }}
                  />
                </Tooltip>
              )}
            </Stack>
          ) : (
            <Typography variant="caption" sx={{ color: tokens.text.muted }}>
              -
            </Typography>
          )}
        </TableCell>

        {/* Run Audit Button */}
        <TableCell align="center">
          <Button
            size="small"
            variant="outlined"
            disabled={isAuditing || isInQueue || documents.length === 0}
            onClick={(e) => {
              e.stopPropagation();
              onRunAudit(cluster._id);
            }}
            sx={{
              fontSize: tokens.fontSize["2xs"],
              minWidth: 60,
              height: 24,
              borderColor: isInQueue ? tokens.text.secondary : tokens.brand.primary,
              color: isInQueue ? tokens.text.secondary : tokens.brand.primary,
              "&:hover": { borderColor: tokens.brand.primary, bgcolor: tokens.status.successBg },
            }}
          >
            {(isAuditing || isInQueue) && (
              <CircularProgress size={12} sx={{ mr: 0.5 }} />
            )}
            {buttonLabel}
          </Button>
        </TableCell>

        {/* Delete Cluster Button */}
        <TableCell align="center" sx={{ width: 48 }}>
          <Tooltip title="Delete folder and all documents">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onDeleteCluster(cluster);
              }}
              disabled={isDeletingCluster}
              sx={{
                color: tokens.text.muted,
                p: 0.5,
                "&:hover": { color: tokens.status.danger, bgcolor: tokens.status.dangerBg },
              }}
            >
              {isDeletingCluster ? (
                <CircularProgress size={16} />
              ) : (
                <img
                  src={DeleteIcon}
                  alt="Delete"
                  style={{ width: 16, height: 16 }}
                />
              )}
            </IconButton>
          </Tooltip>
        </TableCell>
      </TableRow>

      {/* Expanded Document List */}
      {isExpanded && (
        <TableRow>
          <TableCell
            colSpan={COLUMN_COUNT}
            sx={{ p: 0, borderBottom: `1px solid ${tokens.border.light}` }}
          >
            <Collapse in={isExpanded}>
              <Box sx={{ bgcolor: tokens.bg.hover }}>
                {documents.map((doc, index) => (
                  <Box
                    key={doc._id}
                    onClick={() => onDocumentClick?.(doc)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      p: 1.5,
                      pl: 7,
                      cursor: onDocumentClick ? "pointer" : "default",
                      borderBottom:
                        index < documents.length - 1
                          ? `1px solid ${tokens.border.light}`
                          : "none",
                      "&:hover": { bgcolor: tokens.bg.muted },
                    }}
                  >
                    <Description
                      sx={{ color: tokens.text.muted, mr: 1.5, fontSize: 18 }}
                    />

                    {/* Filename */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Tooltip title={doc.filename} arrow>
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: tokens.fontSize.base,
                            fontWeight: tokens.fontWeight.semibold,
                            color: tokens.text.primary,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            maxWidth: "300px",
                          }}
                        >
                          {doc.filename}
                        </Typography>
                      </Tooltip>
                    </Box>

                    {/* Document Type */}
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: tokens.fontSize.base,
                        color: tokens.text.secondary,
                        mr: 2,
                        minWidth: 100,
                      }}
                    >
                      {getDocumentTypeFullLabel(doc.documentType)}
                    </Typography>

                    {/* Status - only show if NOT ready */}
                    {doc.status !== "READY" && (
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: tokens.fontSize.base,
                          color:
                            doc.status === "FAILED" ? tokens.status.danger : tokens.text.secondary,
                          mr: 2,
                          minWidth: 80,
                        }}
                      >
                        {getStatusLabel(doc.status)}
                      </Typography>
                    )}

                    {/* Actions */}
                    <Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
                      {(doc.status === "READY" ||
                        doc.status === "TEXT_EXTRACTED" ||
                        doc.status === "KEYS_EXTRACTED") && (
                        <Tooltip title="Download">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              onDownload(doc);
                            }}
                            disabled={loadingStates.download.includes(doc._id)}
                            sx={{ color: tokens.text.primary, p: 0.5 }}
                          >
                            {loadingStates.download.includes(doc._id) ? (
                              <CircularProgress size={14} />
                            ) : (
                              <Download sx={{ fontSize: 16 }} />
                            )}
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            onDelete(doc);
                          }}
                          disabled={loadingStates.delete.includes(doc._id)}
                          sx={{ color: tokens.text.primary, p: 0.5 }}
                        >
                          {loadingStates.delete.includes(doc._id) ? (
                            <CircularProgress size={14} />
                          ) : (
                            <img
                              src={DeleteIcon}
                              alt="Delete"
                              style={{ width: 14, height: 14 }}
                            />
                          )}
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                ))}
                {documents.length === 0 && (
                  <Box sx={{ p: 2, pl: 7 }}>
                    <Typography variant="body2" sx={{ color: tokens.text.secondary }}>
                      No documents in this folder
                    </Typography>
                  </Box>
                )}
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
};
